import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Typography,
  Box,
  Button,
  Backdrop,
  CircularProgress,
  Alert,
} from '@mui/material';
import PrintIcon from '@mui/icons-material/Print';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RestaurantNumpadModal from './modals/RestaurantNumpadModal';
import TransferTableModal from './modals/TransferTableModal';
import TransferItemModal from './modals/TransferItemModal';
import MergeTablesConfirmModal from './modals/MergeTablesConfirmModal';
import RestaurantKitchenReturnCountdownModal from './modals/RestaurantKitchenReturnCountdownModal';
import DeleteReasonModal from './modals/DeleteReasonModal';
import ItemNoteModal from './modals/ItemNoteModal';
import ConfirmOpenTableModal from '../../restaurant/ConfirmOpenTableModal';
import UnprintedItemsWarningModal from '../../restaurant/components/UnprintedItemsWarningModal';
import ReceiptPrintedNoPermissionModal from '../../restaurant/components/ReceiptPrintedNoPermissionModal';
import ReceiptPrintedConfirmModal from '../../restaurant/components/ReceiptPrintedConfirmModal';
import { CartItem } from '../QuickSaleScreen';

/**
 * RestaurantFlowModals
 *
 * QuickSaleScreen'den taşınan restoran-modu modal/overlay grubu. Davranış
 * birebir korunur: container KOŞULSUZ render edilir; her modalın kendi
 * orijinal guard'ı (isRestaurantMode, tableId, vb.) bu component içinde aynen
 * yaşar. Böylece koşulsuz render edilen modallar (kitchenPrintInfo,
 * noCategoryPrinter, DeleteReason, ItemNote) koşulsuz kalır.
 *
 * Props-bag deseni QuickSaleModals.tsx ile uyumludur.
 */
interface RestaurantFlowModalsProps {
  isRestaurantMode: boolean;
  tableId: any;
  restaurantScreenId: any;
  tableName: any;
  companyId: any;
  cart: CartItem[];
  navigate: any;
  screenSettings: any;

  // Numpad
  numpadModalOpen: boolean;
  numpadProduct: any;
  handleNumpadConfirm: any;
  handleNumpadCancel: any;

  // Transfer table
  transferTableModalOpen: boolean;
  handleCloseTransferTableModal: any;
  handleTransferTable: any;
  handleRequestMerge: any;
  isTransferringTable: boolean;

  // Transfer item
  transferItemModalOpen: boolean;
  setTransferItemModalOpen: any;
  restaurantBranchId: any;
  waiterName: any;
  addWaiterAction: any;

  // Merge tables
  mergeTargetTableId: any;
  mergeTablesModalOpen: boolean;
  handleCloseMergeTablesModal: any;
  handleConfirmMergeTables: any;
  mergeTargetTableName: any;
  mergeSourceItems: any;
  mergeTargetItems: any;
  isMergingTables: boolean;

  // Confirm open table
  confirmOpenTableModalOpen: boolean;
  pendingProduct: any;
  handleConfirmOpenTable: any;
  handleCancelOpenTable: any;
  isOpeningTable: boolean;

  // Unprinted items warning
  unprintedItemsWarningOpen: boolean;
  setUnprintedItemsWarningOpen: any;
  handleContinueWithoutPrinting: any;
  handlePrintToKitchenAndGoBack: any;
  handlePrintCancellationAndGoBack: any;
  handlePrintBothAndGoBack: any;
  unprintedItemsList: any;
  cancelledItemsList: any;

  // Kitchen print info (koşulsuz)
  kitchenPrintInfoOpen: boolean;
  kitchenPrintInfoData: any;

  // No category printer (koşulsuz)
  noCategoryPrinterModalOpen: boolean;
  setNoCategoryPrinterModalOpen: any;
  noCategoryPrinterModalData: any;

  // Kitchen return countdown
  kitchenReturnCountdownOpen: boolean;
  handleKitchenReturnCountdownDismiss: any;
  handleKitchenReturnCountdownTimeout: any;

  // Receipt printed (no permission / confirm)
  receiptPrintedNoPermissionOpen: boolean;
  setReceiptPrintedNoPermissionOpen: any;
  receiptPrintedInfo: any;
  receiptPrintedConfirmOpen: boolean;
  handleReceiptPrintedConfirmCancel: any;
  handleReceiptPrintedConfirm: any;
  pendingProductForReceiptConfirm: any;

  // Delete reason / item note (koşulsuz)
  deleteReasonModalOpen: boolean;
  selectedCartItemId: any;
  handleDeleteReasonConfirm: any;
  setDeleteReasonModalOpen: any;
  itemNoteModalOpen: boolean;
  itemNoteCurrentNote: any;
  handleItemNoteConfirm: any;
  setItemNoteModalOpen: any;

  // Masa yükleme overlay
  isLoadingTableItems: boolean;
  tableLoadError: any;
  handleRetryLoadTableItems: any;
}

const RestaurantFlowModals: React.FC<RestaurantFlowModalsProps> = ({
  isRestaurantMode,
  tableId,
  restaurantScreenId,
  tableName,
  companyId,
  cart,
  navigate,
  screenSettings,
  numpadModalOpen,
  numpadProduct,
  handleNumpadConfirm,
  handleNumpadCancel,
  transferTableModalOpen,
  handleCloseTransferTableModal,
  handleTransferTable,
  handleRequestMerge,
  isTransferringTable,
  transferItemModalOpen,
  setTransferItemModalOpen,
  restaurantBranchId,
  waiterName,
  addWaiterAction,
  mergeTargetTableId,
  mergeTablesModalOpen,
  handleCloseMergeTablesModal,
  handleConfirmMergeTables,
  mergeTargetTableName,
  mergeSourceItems,
  mergeTargetItems,
  isMergingTables,
  confirmOpenTableModalOpen,
  pendingProduct,
  handleConfirmOpenTable,
  handleCancelOpenTable,
  isOpeningTable,
  unprintedItemsWarningOpen,
  setUnprintedItemsWarningOpen,
  handleContinueWithoutPrinting,
  handlePrintToKitchenAndGoBack,
  handlePrintCancellationAndGoBack,
  handlePrintBothAndGoBack,
  unprintedItemsList,
  cancelledItemsList,
  kitchenPrintInfoOpen,
  kitchenPrintInfoData,
  noCategoryPrinterModalOpen,
  setNoCategoryPrinterModalOpen,
  noCategoryPrinterModalData,
  kitchenReturnCountdownOpen,
  handleKitchenReturnCountdownDismiss,
  handleKitchenReturnCountdownTimeout,
  receiptPrintedNoPermissionOpen,
  setReceiptPrintedNoPermissionOpen,
  receiptPrintedInfo,
  receiptPrintedConfirmOpen,
  handleReceiptPrintedConfirmCancel,
  handleReceiptPrintedConfirm,
  pendingProductForReceiptConfirm,
  deleteReasonModalOpen,
  selectedCartItemId,
  handleDeleteReasonConfirm,
  setDeleteReasonModalOpen,
  itemNoteModalOpen,
  itemNoteCurrentNote,
  handleItemNoteConfirm,
  setItemNoteModalOpen,
  isLoadingTableItems,
  tableLoadError,
  handleRetryLoadTableItems,
}) => {
  return (
    <>
      {/* ✅ YENİ: Numpad Modalı (Restaurant Modu - Miktar Girişi) */}
      {isRestaurantMode && (
        <RestaurantNumpadModal
          open={numpadModalOpen}
          productName={numpadProduct?.name || ''}
          productImage={numpadProduct?.image}
          onConfirm={handleNumpadConfirm}
          onCancel={handleNumpadCancel}
        />
      )}

      {/* ✅ YENİ: Masa Aktarma Modalı (Restaurant Modu) */}
      {isRestaurantMode && tableId && restaurantScreenId && (
        <TransferTableModal
          open={transferTableModalOpen}
          onClose={handleCloseTransferTableModal}
          onConfirm={handleTransferTable}
          onRequestMerge={handleRequestMerge} // ✅ YENİ: Birleştirme talebi
          currentTableId={Number(tableId)}
          currentTableName={tableName}
          restaurantScreenId={restaurantScreenId}
          isProcessing={isTransferringTable}
        />
      )}

      {/* Ürün Aktarma Modalı (Restaurant Modu) */}
      {isRestaurantMode && tableId && restaurantScreenId && (
        <TransferItemModal
          open={transferItemModalOpen}
          onClose={() => setTransferItemModalOpen(false)}
          cart={cart}
          currentTableId={Number(tableId)}
          currentTableName={tableName}
          restaurantScreenId={restaurantScreenId}
          restaurantBranchId={restaurantBranchId}
          waiterName={waiterName}
          companyId={companyId}
          onTransferComplete={() => {
            setTransferItemModalOpen(false);
            navigate(`/restaurant-screen/${restaurantScreenId}`);
          }}
          addWaiterAction={addWaiterAction}
        />
      )}

      {/* ✅ YENİ: Masa Birleştirme Onay Modalı (Restaurant Modu) */}
      {isRestaurantMode && mergeTargetTableId && (
        <MergeTablesConfirmModal
          open={mergeTablesModalOpen}
          onClose={handleCloseMergeTablesModal}
          onConfirm={handleConfirmMergeTables}
          sourceTableName={tableName}
          targetTableName={mergeTargetTableName}
          sourceItems={mergeSourceItems}
          targetItems={mergeTargetItems}
          isProcessing={isMergingTables}
        />
      )}

      {/* ✅ YENİ: Adisyon Açma Onay Modalı (Restaurant Modu) */}
      {isRestaurantMode && (
        <ConfirmOpenTableModal
          open={confirmOpenTableModalOpen}
          tableName={tableName}
          productName={pendingProduct?.name || ''}
          waiterName={waiterName}
          onConfirm={handleConfirmOpenTable}
          onCancel={handleCancelOpenTable}
          isLoading={isOpeningTable} // ✅ YENİ: Loading state
        />
      )}

      {/* ✅ YENİ: Yazdırılmamış/İptal Ürün Uyarı Modalı (Restaurant Modu) */}
      {isRestaurantMode && (
        <UnprintedItemsWarningModal
          open={unprintedItemsWarningOpen}
          onClose={() => setUnprintedItemsWarningOpen(false)}
          onContinue={handleContinueWithoutPrinting}
          onPrintToKitchen={handlePrintToKitchenAndGoBack}
          onPrintCancellation={handlePrintCancellationAndGoBack}
          onPrintBoth={handlePrintBothAndGoBack}
          unprintedItems={unprintedItemsList}
          cancelledItems={cancelledItemsList}
          tableName={tableName}
        />
      )}

      {/* Mutfak Yazdırma Bilgi Modalı */}
      <Dialog
        open={kitchenPrintInfoOpen}
        PaperProps={{
          sx: { borderRadius: 3, minWidth: 350, maxWidth: 450 }
        }}
      >
        <DialogTitle sx={{ bgcolor: 'info.main', color: 'white', py: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
          <PrintIcon />
          <Typography variant="h6" fontWeight="bold">Mutfağa Yazdırılıyor</Typography>
        </DialogTitle>
        <DialogContent sx={{ pt: 2, pb: 2 }}>
          {kitchenPrintInfoData.printedItems.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" color="success.main" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                <CheckCircleIcon fontSize="small" /> Daha Önce Yazdırılmış:
              </Typography>
              {kitchenPrintInfoData.printedItems.map((item: any, i: number) => (
                <Typography key={i} variant="body2" sx={{ pl: 3, color: 'text.secondary' }}>
                  • {item.name} × {item.quantity}
                </Typography>
              ))}
            </Box>
          )}
          <Box>
            <Typography variant="subtitle2" color="warning.main" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
              <PrintIcon fontSize="small" /> Şimdi Yazdırılıyor:
            </Typography>
            {kitchenPrintInfoData.unprintedItems.map((item: any, i: number) => (
              <Typography key={i} variant="body2" sx={{ pl: 3, fontWeight: 'bold' }}>
                • {item.name} × {item.quantity}
              </Typography>
            ))}
          </Box>
          {/* ✅ YENİ: Yazıcı atanmamış kategorideki ürünler — yazdırılmadı */}
          {kitchenPrintInfoData.skippedItems && kitchenPrintInfoData.skippedItems.length > 0 && (
            <Box sx={{ mt: 2, p: 1.5, bgcolor: 'warning.lighter', border: '1px dashed', borderColor: 'warning.main', borderRadius: 1 }}>
              <Typography variant="subtitle2" color="error.main" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                ⚠️ Yazıcı Atanmamış (Yazdırılamadı):
              </Typography>
              {kitchenPrintInfoData.skippedItems.map((item: any, i: number) => (
                <Typography key={i} variant="body2" sx={{ pl: 3 }}>
                  • {item.name} × {item.quantity}
                  {item.categoryName ? <span style={{ color: '#888', fontStyle: 'italic' }}> [{item.categoryName}]</span> : null}
                </Typography>
              ))}
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, pl: 3 }}>
                Bu ürünlerin kategorilerine yazıcı atayın.
              </Typography>
            </Box>
          )}
        </DialogContent>
      </Dialog>

      {/* ✅ YENİ: Tüm ürünlerin kategorisinde yazıcı atanmamış — uyarı modalı */}
      <Dialog
        open={noCategoryPrinterModalOpen}
        onClose={() => setNoCategoryPrinterModalOpen(false)}
        PaperProps={{ sx: { borderRadius: 3, minWidth: 380, maxWidth: 480 } }}
      >
        <DialogTitle sx={{ bgcolor: 'warning.main', color: 'white', py: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
          <PrintIcon />
          <Typography variant="h6" fontWeight="bold">Yazıcı Ataması Yapılmamış</Typography>
        </DialogTitle>
        <DialogContent sx={{ pt: 2.5, pb: 1 }}>
          <Typography variant="body1" sx={{ mb: 1.5 }}>
            {noCategoryPrinterModalData.categoryNames.length > 1
              ? 'Aşağıdaki kategorilere yazıcı atanmamış. Bu kategorilerdeki ürünler yazdırılamadı:'
              : 'Bu kategoriye yazıcı atanmamış. Bu kategorideki ürünler yazdırılamadı:'}
          </Typography>
          <Box sx={{ pl: 1, mb: 1.5 }}>
            {noCategoryPrinterModalData.categoryNames.map((cat: any, i: number) => (
              <Typography key={i} variant="subtitle2" sx={{ fontWeight: 'bold', color: 'warning.dark' }}>
                • {cat}
              </Typography>
            ))}
          </Box>
          {noCategoryPrinterModalData.skippedItems.length > 0 && (
            <Box sx={{ p: 1, bgcolor: 'grey.50', borderRadius: 1, mb: 1 }}>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                Etkilenen ürünler:
              </Typography>
              {noCategoryPrinterModalData.skippedItems.map((it: any, i: number) => (
                <Typography key={i} variant="body2" sx={{ pl: 1 }}>
                  • {it.name} × {it.quantity}
                </Typography>
              ))}
            </Box>
          )}
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
            Lütfen <b>Hızlı Satış Ayarları → Yazıcılar → Kategori Yazıcı Atamaları</b> bölümünden bu kategorilere yazıcı atayın ve tekrar deneyin.
          </Typography>
        </DialogContent>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 2, pt: 1 }}>
          <Button
            variant="contained"
            color="warning"
            onClick={() => setNoCategoryPrinterModalOpen(false)}
            sx={{ minWidth: 100 }}
          >
            Tamam
          </Button>
        </Box>
      </Dialog>

      {isRestaurantMode && (
        <RestaurantKitchenReturnCountdownModal
          open={kitchenReturnCountdownOpen}
          onDismiss={handleKitchenReturnCountdownDismiss}
          onTimeout={handleKitchenReturnCountdownTimeout}
        />
      )}

      {/* ✅ YENİ: Adisyon Yazdırıldı - Yetki Yok Modalı */}
      {isRestaurantMode && (
        <ReceiptPrintedNoPermissionModal
          open={receiptPrintedNoPermissionOpen}
          onClose={() => setReceiptPrintedNoPermissionOpen(false)}
          tableName={tableName}
          receiptPrintedAt={receiptPrintedInfo.receiptPrintedAt}
          receiptPrintedBy={receiptPrintedInfo.receiptPrintedBy}
        />
      )}

      {/* ✅ YENİ: Adisyon Yazdırıldı - Onay Modalı (Yetkili Garsonlar İçin) */}
      {isRestaurantMode && (
        <ReceiptPrintedConfirmModal
          open={receiptPrintedConfirmOpen}
          onClose={handleReceiptPrintedConfirmCancel}
          onConfirm={handleReceiptPrintedConfirm}
          tableName={tableName || 'Masa'}
          productName={pendingProductForReceiptConfirm?.name}
        />
      )}

      {/* Ürün Silme Nedeni Modalı (Restoran Modu) */}
      <DeleteReasonModal
        open={deleteReasonModalOpen}
        productName={
          (selectedCartItemId && cart.find(item => item.cartItemId === selectedCartItemId)?.name) || ''
        }
        predefinedReasons={screenSettings?.predefinedDeleteReasons || []}
        onConfirm={handleDeleteReasonConfirm}
        onCancel={() => setDeleteReasonModalOpen(false)}
      />

      <ItemNoteModal
        open={itemNoteModalOpen}
        productName={
          (selectedCartItemId && cart.find(item => item.cartItemId === selectedCartItemId)?.name) || ''
        }
        currentNote={itemNoteCurrentNote}
        onConfirm={handleItemNoteConfirm}
        onCancel={() => setItemNoteModalOpen(false)}
      />

      {/* ✅ YENİ: Restaurant modu — masa ürünleri yükleme overlay'i (loading + hata + manuel retry) */}
      {isRestaurantMode && (isLoadingTableItems || !!tableLoadError) && (
        <Backdrop
          open
          sx={{
            color: '#fff',
            zIndex: (theme) => theme.zIndex.modal + 10,
            backdropFilter: 'blur(2px)',
            backgroundColor: 'rgba(0,0,0,0.55)',
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            p: 3,
            textAlign: 'center',
          }}
        >
          {isLoadingTableItems ? (
            <>
              <CircularProgress color="inherit" size={56} thickness={4} />
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Masa ürünleri yükleniyor...
              </Typography>
              {tableName && (
                <Typography variant="body2" sx={{ opacity: 0.85 }}>
                  {tableName}
                </Typography>
              )}
              <Typography variant="caption" sx={{ opacity: 0.7, maxWidth: 360 }}>
                Bağlantı yavaşsa otomatik olarak 3 kez denenecektir. Lütfen bekleyiniz.
              </Typography>
            </>
          ) : tableLoadError ? (
            <>
              <Alert
                severity="error"
                variant="filled"
                sx={{ maxWidth: 480, width: '100%' }}
              >
                {tableLoadError}
              </Alert>
              <Box sx={{ display: 'flex', gap: 1.5, mt: 1 }}>
                <Button
                  variant="contained"
                  color="primary"
                  size="large"
                  onClick={handleRetryLoadTableItems}
                >
                  Yeniden Dene
                </Button>
                <Button
                  variant="outlined"
                  color="inherit"
                  size="large"
                  onClick={() => {
                    if (restaurantScreenId) {
                      navigate(`/restaurant-screen/${restaurantScreenId}`);
                    }
                  }}
                >
                  Masaya Geri Dön
                </Button>
              </Box>
            </>
          ) : null}
        </Backdrop>
      )}
    </>
  );
};

export default RestaurantFlowModals;
